import { useState } from "react";
import { Button } from "./ui/button";
import { Check, X, Loader2 } from "lucide-react";
import GeheugenVoorstel from "./GeheugenVoorstel";
import BevestigAantal from "./BevestigAantal";
import { beslisGroep } from "@/lib/geheugenGroep";
import { useTaal } from "@/contexts/TaalContext";

/** Een groep opruimvoorstellen als EEN kaart. Herstelbatch b en c (GO Frank 23 okt).
 *
 *  Op 23 oktober waren 170 open voorstellen 45 groepen regels die op elkaar leken;
 *  "Blijft: Answer in Dutch language" stond op meerdere kaarten. De groep heeft een
 *  vakje voor een selectie, twee knoppen voor de hele groep met eerst een vraag met
 *  het aantal, en per voorstel blijft de gewone kaart staan.
 */
export default function GeheugenGroep({ groep, gekozen = false, onKies, onBesloten }) {
  const { t } = useTaal();
  const [vraag, setVraag] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState("");
  const [open, setOpen] = useState(groep.ids.length === 1);
  const samen = groep.actie === "samenvoegen";
  const aantal = groep.ids.length;

  const voerUit = async () => {
    setBezig(true);
    setFout("");
    try {
      await beslisGroep(groep.ids, vraag);
      setVraag(null);
      onBesloten?.();
    } catch (err) {
      setFout(err?.response?.data?.detail || t("Er ging iets mis; er is niets veranderd."));
    } finally {
      setBezig(false);
    }
  };

  return (
    <div className="border rounded-lg p-3 space-y-2" data-testid="geheugen-groep">
      <label className="flex items-start gap-2 text-xs text-[hsl(var(--muted-foreground))]">
        <input type="checkbox" className="mt-0.5 h-4 w-4" checked={gekozen}
               onChange={(e) => onKies?.(e.target.checked)} data-testid="geheugen-groep-kies" />
        <span>
          {aantal === 1
            ? t("Een voorstel om je geheugen op te ruimen.")
            : t("{n} voorstellen over regels die op elkaar lijken.", { n: aantal })}
          {" "}{samen
            ? t("Samenvoegen houdt de regel die het meest gebruikt is.")
            : t("Vervangen houdt de nieuwste regel.")}
          {" "}{t("Er gebeurt niets tot je kiest.")}
        </span>
      </label>
      {fout && <p className="text-xs text-[hsl(var(--destructive))]" data-testid="geheugen-groep-fout">{fout}</p>}
      {vraag ? (
        <BevestigAantal
          tekst={vraag === "vervangen"
            ? t("{n} voorstellen vervangen? De regels die weggaan, zijn daarna weg.", { n: aantal })
            : t("{n} voorstellen houden? Alle regels blijven staan.", { n: aantal })}
          bezig={bezig} onJa={voerUit} onNee={() => setVraag(null)} />
      ) : aantal > 1 && (
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" className="min-h-11 px-4" disabled={bezig}
                  onClick={() => setVraag("vervangen")} data-testid="geheugen-groep-vervangen">
            {bezig ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Check className="h-3 w-3 mr-1" />}
            {samen ? t("Voeg deze groep samen") : t("Vervang deze groep")}
          </Button>
          <Button size="sm" variant="outline" className="min-h-11 px-4" disabled={bezig}
                  onClick={() => setVraag("houden")} data-testid="geheugen-groep-houden">
            <X className="h-3 w-3 mr-1" /> {t("Houd deze groep")}
          </Button>
          <Button size="sm" variant="ghost" className="min-h-11 px-3" onClick={() => setOpen((x) => !x)}
                  data-testid="geheugen-groep-open">
            {open ? t("Verberg de voorstellen") : t("Toon de {n} voorstellen", { n: aantal })}
          </Button>
        </div>
      )}
      {open && groep.voorstellen.map((v) => (
        <GeheugenVoorstel key={v.id} voorstel={v} onBesloten={onBesloten} />
      ))}
    </div>
  );
}
